/**
 * Utility functions for handling secure post-login redirects
 */

/**
 * Validates that a redirect URL is safe for our application
 * @param redirectUrl - The URL to validate
 * @returns boolean - True if the URL is safe to redirect to
 */
export function isValidRedirectUrl(redirectUrl: string): boolean {
  if (!redirectUrl) return false;
  
  try {
    // If it's a relative URL, it's safe
    if (redirectUrl.startsWith('/')) {
      // Additional safety check - ensure it doesn't try to redirect to external sites
      // using protocol-relative URLs like //evil.com
      if (redirectUrl.startsWith('//')) {
        return false;
      }
      return true;
    }
    
    // If it's an absolute URL, check if it's our domain
    const url = new URL(redirectUrl);
    const currentOrigin = window.location.origin;
    
    // Only allow redirects to the same origin
    return url.origin === currentOrigin;
  } catch (error) {
    // If URL parsing fails, it's not safe
    return false;
  }
}

/**
 * Gets the redirect URL from URL parameters, with validation
 * @param searchParams - URLSearchParams object
 * @returns string - The validated redirect URL or null if invalid
 */
export function getValidatedRedirectUrl(searchParams: URLSearchParams): string | null {
  const redirect = searchParams.get('redirect');
  
  if (!redirect) {
    return null;
  }
  
  // Decode the URL parameter
  const decodedRedirect = decodeURIComponent(redirect);
  
  // Validate the redirect URL
  if (isValidRedirectUrl(decodedRedirect)) {
    return decodedRedirect;
  }
  
  return null;
}

/**
 * Gets the default redirect URL after login
 * @returns string - The default redirect URL
 */
export function getDefaultRedirectUrl(): string {
  return '/dashboard';
}

/**
 * Stores the intended destination before redirecting to login
 * @param intendedUrl - The URL the user was trying to access
 */
export function storeIntendedDestination(intendedUrl: string): void {
  if (isValidRedirectUrl(intendedUrl)) {
    sessionStorage.setItem('intendedDestination', intendedUrl);
  }
}

/**
 * Retrieves and clears the intended destination
 * @returns string | null - The intended destination URL or null if not set
 */
export function getAndClearIntendedDestination(): string | null {
  const intended = sessionStorage.getItem('intendedDestination');
  if (intended) {
    sessionStorage.removeItem('intendedDestination');
    return isValidRedirectUrl(intended) ? intended : null;
  }
  return null;
}

/**
 * Determines the post-login redirect URL, checking multiple sources
 * @param searchParams - URLSearchParams from current URL
 * @returns string - The URL to redirect to after login
 */
export function getPostLoginRedirectUrl(searchParams: URLSearchParams): string {
  // First, check URL parameter
  const urlRedirect = getValidatedRedirectUrl(searchParams);
  if (urlRedirect) {
    return urlRedirect;
  }
  
  // Second, check session storage
  const storedRedirect = getAndClearIntendedDestination();
  if (storedRedirect) {
    return storedRedirect;
  }
  
  // Finally, use default
  return getDefaultRedirectUrl();
}